import { parseDbTimestamp } from "@repo/shared";
import type { ClientFile } from "@repo/shared/types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { formatBytes } from "@/lib/utils";
import { T } from "./file-summary-dialog.i18n";

// Full AI summary + extracted text of a processed client file.
export function FileSummaryDialog({
  file,
  onClose,
}: {
  file: ClientFile | null;
  onClose: () => void;
}) {
  const { formatDate } = useRegionalFormat();
  const t = useT(T);
  // Size, uploader and date — the list row gives its Summary cell to the AI
  // summary, so this is where a processed file's size is shown.
  const meta = file
    ? [formatBytes(file.size), file.uploadedBy?.name, formatDate(parseDbTimestamp(file.createdAt))]
        .filter(Boolean)
        .join(" \u2022 ")
    : "";

  return (
    <Dialog open={!!file} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{file?.fileName}</DialogTitle>
          <DialogDescription>{meta}</DialogDescription>
        </DialogHeader>
        {file && (
          <div className="space-y-4 overflow-y-auto pr-1">
            {file.summary && (
              <div>
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
                  {t.summaryHeading}
                </div>
                <p className="text-sm whitespace-pre-line">{file.summary}</p>
              </div>
            )}
            {file.extractedText && (
              <div>
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
                  {t.transcriptHeading}
                </div>
                <pre className="text-xs whitespace-pre-wrap font-mono bg-muted/30 p-3 rounded-md max-h-[40vh] overflow-y-auto">
                  {file.extractedText}
                </pre>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
